import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "@/shared/lib/cn";
import { Spinner } from "@/shared/ui/spinner";

export const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--radius-sm)] text-sm font-bold tracking-wide transition-[background-color,border-color,color,transform,opacity] duration-[var(--motion-fast)] ease-[var(--motion-ease)] outline-none focus-visible:ring-2 focus-visible:ring-ring/70 disabled:pointer-events-none disabled:opacity-45 active:not-disabled:translate-y-px [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border border-primary bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        secondary:
          "border border-accent bg-accent text-accent-foreground hover:bg-accent/85",
        outline:
          "border border-border bg-surface/55 text-foreground hover:border-foreground-muted hover:bg-surface-raised",
        ghost:
          "border border-transparent bg-transparent text-foreground-muted hover:bg-surface-raised hover:text-foreground",
        destructive:
          "border border-danger bg-danger text-danger-foreground hover:bg-danger/90",
        scoringHit:
          "border border-primary bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        scoringOut:
          "border border-danger bg-surface text-foreground hover:bg-danger/15",
        scoringReach:
          "border border-accent bg-accent text-accent-foreground hover:bg-accent/85",
        selected:
          "border border-warning bg-warning text-warning-foreground shadow-sm",
      },
      size: {
        xs: "h-7 px-2 text-xs",
        sm: "h-[var(--control-secondary)] px-3 text-xs",
        default: "h-[var(--control-default)] px-3.5",
        scoring: "h-[var(--control-scoring)] min-w-0 px-2 text-base",
        lg: "h-11 px-5 text-base",
        icon: "size-[var(--control-secondary)] p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    pending?: boolean;
    pendingLabel?: ReactNode;
  };

export function Button({
  asChild = false,
  className,
  disabled,
  pending = false,
  pendingLabel,
  size = "default",
  variant = "default",
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  const hasPendingTreatment = pending || pendingLabel !== undefined;
  return (
    <Comp
      data-pending={pending || undefined}
      data-size={size}
      data-slot="button"
      data-variant={variant}
      aria-busy={pending || undefined}
      className={cn(
        buttonVariants({ className, size, variant }),
        pending && "cursor-wait",
      )}
      disabled={disabled || pending}
      {...props}
    >
      {hasPendingTreatment ? (
        <span className="inline-grid min-w-0 grid-cols-1 grid-rows-1 place-items-center">
          <span
            aria-hidden={pending || undefined}
            className={cn(
              "col-start-1 row-start-1 inline-flex min-w-0 items-center justify-center gap-1.5",
              pending && "invisible",
            )}
          >
            {children}
          </span>
          <span
            aria-hidden={!pending || undefined}
            className={cn(
              "col-start-1 row-start-1 inline-flex min-w-0 items-center justify-center gap-1.5",
              !pending && "invisible",
            )}
          >
            <Spinner decorative />
            {pendingLabel ?? children}
          </span>
        </span>
      ) : (
        children
      )}
    </Comp>
  );
}
